<template>
  <TransitionRoot appear :show="isOpen" as="template">
    <Dialog as="div" @close="closeDialog" class="relative z-50">
      <TransitionChild
        as="template"
        enter="duration-300 ease-out"
        enter-from="opacity-0"
        enter-to="opacity-100"
        leave="duration-200 ease-in"
        leave-from="opacity-100"
        leave-to="opacity-0"
      >
        <div class="fixed inset-0 bg-gray-900 bg-opacity-40 pointer-events-none" />
      </TransitionChild>

      <div class="fixed inset-0 overflow-y-auto pointer-events-none">
        <div class="flex min-h-full items-center justify-center p-4 text-center pointer-events-none">
          <TransitionChild
            as="template"
            enter="duration-300 ease-out"
            enter-from="opacity-0 scale-95"
            enter-to="opacity-100 scale-100"
            leave="duration-200 ease-in"
            leave-from="opacity-100 scale-100"
            leave-to="opacity-0 scale-95"
          >
            <DialogPanel
              class="w-full max-w-md transform overflow-hidden rounded-2xl bg-white p-8 text-left align-middle shadow-xl transition-all border border-brand-blue/10 pointer-events-auto mx-4"
            >
              <!-- Header close -->
              <div class="flex justify-end -mt-2 -mr-2 mb-2">
                <button
                  @click="closeDialog"
                  class="cursor-pointer group w-8 h-8 rounded-full group-hover:bg-emerald-50 grid place-items-center"
                  :disabled="isLoading"
                >
                  <X class="size-5 text-slate-500 group-hover:text-emerald-600 group-hover:scale-125" />
                </button>
              </div>

              <!-- Centered content -->
              <div class="flex flex-col items-center text-center">
                <div class="relative mb-6">
                  <div class="w-16 h-16 flex items-center justify-center rounded-2xl shadow-lg border bg-brand-blue/10 border-brand-blue/20">
                    <Shield class="w-8 h-8 text-brand-blue" />
                  </div>
                  <div class="absolute -top-1 -right-1 w-4 h-4 bg-brand-blue/30 rounded-full animate-pulse"></div>
                  <div class="absolute -bottom-1 -left-1 w-3 h-3 bg-brand-blue/30 rounded-full animate-pulse delay-1000"></div>
                </div>

                <h3 class="text-xl font-bold text-brand-blue mb-2 tracking-tight">Two-Factor Authentication</h3>
                <p class="text-gray-600 mb-6 leading-relaxed">
                  We've sent a 6-digit verification code to your email address. 
                  Enter it below to complete your login.
                </p>

                <!-- Timer -->
                <div v-if="remainingTime > 0" class="mb-6">
                  <div class="inline-flex items-center px-4 py-2 rounded-full bg-yellow-100 text-yellow-800 text-sm font-medium">
                    <Clock class="w-4 h-4 mr-2" />
                    Code expires in {{ formatTime(remainingTime) }}
                  </div>
                </div>

                <!-- Code Input -->
                <div v-if="!showBackupCode" class="w-full mb-6">
                  <input
                    ref="codeInput"
                    v-model="verificationCode"
                    type="text"
                    maxlength="6"
                    :class="[
                      'w-full px-4 py-4 text-center text-2xl font-mono tracking-widest border-2 rounded-xl focus:ring-2 focus:ring-brand-blue focus:border-brand-blue transition-all duration-200 hover:border-gray-400 cursor-text',
                      errorMessage ? 'border-red-400 bg-red-50 focus:ring-red-500 focus:border-red-500' : 'border-gray-300'
                    ]"
                    placeholder="000000"
                    :disabled="isLoading"
                    @input="handleCodeInput"
                    @keydown.enter="verifyCode"
                    @focus="handleFocus"
                    @blur="handleBlur"
                  />
                  <p class="text-xs text-gray-500 mt-2">
                    Enter the 6-digit code from your email
                  </p>
                </div>

                <!-- Backup Code Option -->
                <div class="w-full mb-6">
                  <button
                    @click="showBackupCode = !showBackupCode"
                    class="text-sm text-brand-blue hover:text-brand-blue/80 transition-colors"
                  >
                    {{ showBackupCode ? 'Hide' : 'Use backup code instead' }}
                  </button>
                  
                  <div v-if="showBackupCode" class="mt-3">
                    <input
                      v-model="backupCode"
                      type="text"
                      maxlength="12"
                      :class="[
                        'w-full px-4 py-3 text-center text-lg font-mono tracking-wider border-2 rounded-xl focus:ring-2 focus:ring-brand-blue focus:border-brand-blue transition-all duration-200',
                        errorMessage ? 'border-red-400 bg-red-50 focus:ring-red-500 focus:border-red-500' : 'border-gray-300'
                      ]"
                      placeholder="123456789012"
                      :disabled="isLoading"
                      @keydown.enter="verifyBackupCode"
                    />
                    <p class="text-xs text-gray-500 mt-2">
                      Enter a 12-character backup code
                    </p>
                  </div>
                </div>

                <!-- Error Message -->
                <div v-if="errorMessage" class="w-full mb-4">
                  <div class="bg-red-50 border border-red-200 rounded-xl p-4 shadow-sm">
                    <div class="flex items-start">
                      <div class="flex-shrink-0">
                      </div>
                      <div class="ml-3 flex-1">
                        <h3 class="text-sm font-semibold text-red-800">Verification Failed</h3>
                        <p class="mt-1 text-sm text-red-700">{{ errorMessage }}</p>
                      </div>
                      <div class="ml-3 flex-shrink-0">
                        <button @click="() => clearError()" class="inline-flex text-red-400 hover:text-red-500 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 rounded-md transition-colors duration-200">
                          <span class="sr-only">Dismiss</span>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Action Buttons -->
                <div class="w-full space-y-3">
                  <button
                    @click="verifyCode"
                    :disabled="isLoading || (!verificationCode && !backupCode)"
                    class="w-full bg-brand-blue hover:bg-brand-blue/90 disabled:bg-gray-300 text-white px-6 py-3 rounded-xl font-semibold transition-all duration-200 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-brand-blue focus:ring-offset-2"
                  >
                    <span v-if="isLoading" class="flex items-center justify-center">
                      <div class="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                      Signing in...
                    </span>
                    <span v-else>Verify & Continue</span>
                  </button>
                  
                  <button
                    @click="resendCode"
                    :disabled="isLoading || remainingTime > 0"
                    class="w-full px-6 py-3 border border-gray-300 text-gray-700 rounded-xl font-medium hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Resend Code
                  </button>
                </div>
              </div>
            </DialogPanel>
          </TransitionChild>
        </div>
      </div>
    </Dialog>
  </TransitionRoot>
</template>

<script setup lang="ts">
import { ref, watch, nextTick, onMounted, onUnmounted } from 'vue'
import { Dialog, DialogPanel, TransitionChild, TransitionRoot } from "@headlessui/vue"
import { Shield, X, Clock } from 'lucide-vue-next'

interface Props {
  isOpen: boolean
  isLoading?: boolean
  userEmail?: string
  remainingTime?: number
}

interface Emits {
  (e: 'close'): void
  (e: 'verify', code: string): void
  (e: 'verify-backup', code: string): void
  (e: 'resend'): void
}

const props = withDefaults(defineProps<Props>(), {
  isLoading: false,
  remainingTime: 0
})

const emit = defineEmits<Emits>()

// State
const verificationCode = ref('')
const backupCode = ref('')
const showBackupCode = ref(false)
const errorMessage = ref('')
const codeInput = ref<HTMLInputElement>()
const isInputFocused = ref(false)

// Timer
let timer: NodeJS.Timeout | null = null

// Watch for dialog open/close
watch(() => props.isOpen, (isOpen) => {
  if (isOpen) {
    nextTick(() => {
      codeInput.value?.focus()
    })
    resetForm()
  } else {
    clearTimer()
  }
})

// Watch remaining time for timer
watch(() => props.remainingTime, (time) => {
  if (time > 0 && props.isOpen) {
    startTimer()
  } else {
    clearTimer()
  }
})

// Methods
const handleCodeInput = (event: Event) => {
  const target = event.target as HTMLInputElement
  const value = target.value.replace(/\D/g, '') // Only allow digits
  verificationCode.value = value
  
  // Clear error when user starts typing
  if (errorMessage.value) {
    errorMessage.value = ''
  }
}

const handleFocus = () => {
  isInputFocused.value = true
}

const handleBlur = () => {
  isInputFocused.value = false
}

const verifyCode = () => {
  if (!verificationCode.value) {
    errorMessage.value = 'Please enter a verification code'
    return
  }
  
  emit('verify', verificationCode.value)
}

const verifyBackupCode = () => {
  if (!backupCode.value) {
    errorMessage.value = 'Please enter a backup code'
    return
  }
  
  emit('verify-backup', backupCode.value)
}

const resendCode = () => {
  emit('resend')
  resetForm()
}

const closeDialog = () => {
  if (!props.isLoading) {
    emit('close')
  }
}

const resetForm = () => {
  verificationCode.value = ''
  backupCode.value = ''
  showBackupCode.value = false
  errorMessage.value = ''
}

const startTimer = () => {
  clearTimer()
  timer = setInterval(() => {
    // Timer is handled by parent component
  }, 1000)
}

const clearTimer = () => {
  if (timer) {
    clearInterval(timer)
    timer = null
  }
}

const formatTime = (seconds: number) => {
  const mins = Math.floor(seconds / 60)
  const secs = seconds % 60
  return `${mins}:${secs.toString().padStart(2, '0')}`
}

// Cleanup
onUnmounted(() => {
  clearTimer()
})

// Expose methods for parent
defineExpose({
  setError: (message: string) => {
    errorMessage.value = message
  },
  clearError: () => {
    errorMessage.value = ''
  }
})
</script>
